"use client";

import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";

import {
  Code2,
  Briefcase,
  GraduationCap,
  Github,
  Mail,
  MapPin,
  Brain,
} from "lucide-react";
import Link from "next/link";
import { glassCardVariants } from "./glass-card";
import GlassCard from "./glass-card";
import { env } from "@/lib/env";
import Image from "next/image";
import { images } from "@/lib/constants";
import ProjectsShowcase from "@/components/creations/projects/projects-showcase";
import TechTools from "@/components/creations/tech-tools";

interface featureProps {
  title: string;
  description: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const features: featureProps[] = [
  {
    title: "Full-Stack Development",
    description:
      "Expertise in M.E.R.N stack, Next.js, React Native, and modern web technologies. Building scalable applications with TypeScript and Node.js.",
    Icon: Code2,
  },
  {
    title: "AI & Innovation",
    description:
      "Leveraging AI technologies including OpenAI API, prompt engineering, and machine learning to create intelligent, user-centric solutions.",
    Icon: GraduationCap,
  },
  {
    title: "Professional Experience",
    description:
      "Proven track record from internship at Emesa Research, optimizing databases and leading website development projects.",
    Icon: Briefcase,
  },
  {
    title: "Creative Problem Solver",
    description:
      "From Unity game development to SaaS applications, combining creativity with technical expertise to build innovative solutions.",
    Icon: Brain,
  },
];

export default function Hero() {
  const handleDownloadCV = () => {
    // Create a link element and trigger download
    const link = document.createElement("a");
    link.href = "/brian_otieno's_cv.pdf"; // Make sure to place your CV in the public folder
    link.download = "Brian_Otieno's_CV.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* Hero Section */}
      <section className="relative overflow-hidden py-15 px-4">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="flex flex-col items-center text-center space-y-8">
            {/* Badge */}
            <Badge
              className={glassCardVariants({
                className: "font-black text-foreground",
              })}
            >
              <span className="">Full-Stack Developer & UI/UX Designer</span>
            </Badge>

            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-outline-white-black text-5xl md:text-7xl font-black">
                Brian Otieno
              </h1>
            </div>

            <div className="w-full grid md:flex md:items-center md:justify-center md:gap-8 space-y-6 md:space-y-0">
              <Image
                src={`/images/${images[0]}`}
                alt="Image"
                width={100}
                height={100}
                className="size-70 hidden md:block col-span-1 lg:col-span-4 mx-auto"
              />
              <p className="max-w-[80vw] md:max-w-175 text-outline-black-white leading-relaxed text-xl">
                A B.SC Computer Science student from Moi University. Getting
                better at innovation, with each passing day. Transforming
                ideas into reality through code and creative problem-solving.
              </p>
            </div>

            {/* Contact Info */}
            <div className="flex flex-wrap justify-center gap-4 text-sm bg-card p-2 rounded-lg text-foreground/70">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <span>{env.NEXT_PUBLIC_LOCATION}</span>
              </div>
              {/* <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-primary" />
                <a
                  href="tel:+254"
                  className="hover:text-primary transition-colors"
                >
                  +254
                </a>
              </div> */}
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                <a
                  href={`mailto:${env.NEXT_PUBLIC_CONTACT_EMAIL}`}
                  className="hover:text-primary transition-colors"
                >
                  {env.NEXT_PUBLIC_CONTACT_EMAIL}
                </a>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              {/* <button
                onClick={handleDownloadCV}
                className={glassCardVariants({
                  className:
                    "text-foreground flex items-center justify-center group px-3 hover:text-primary hover:border-primary cursor-pointer",
                })}
              >
                <ArrowDown className="h-5 w-5 mr-2 group-hover:hidden" />
                <MoveDown className="h-5 w-5 mr-2 hidden group-hover:block animate-bounce" />
                Download CV
              </button> */}
              <Link
                href="/projects"
                className={buttonVariants({
                  size: "lg",
                  variant: "secondary",
                })}
              >
                View Projects
              </Link>
              <Link
                href="https://github.com/oluoko"
                target="_blank"
                rel="noopener noreferrer"
                className={buttonVariants({
                  variant: "opposite",
                })}
              >
                <Github className="h-5 w-5 mr-2" />
                GitHub
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features/Skills Section */}
      <section className="px-4 pb-20 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {features.map((feature, index) => (
            <GlassCard
              key={index}
              variant="default"
              blur="md"
              glow="subtle"
              hover
              className="p-6"
            >
              <div className="mb-4">
                <feature.Icon className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-outline-fg-primary-soft text-lg font-semibold mb-3">
                {feature.title}
              </h3>
              <p className="text-sm">{feature.description}</p>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* Tech Stack Showcase */}
      <section className="px-4 pb-20 max-w-6xl mx-auto">
        <div className="grid group p-0 m-0 cursor-pointer">
          <div className="p-8 md:p-12 bg-card border-t  group-hover:border-t-primary ">
            <h3 className="text-foreground text-2xl md:text-3xl font-bold mb-6 text-center">
              Tech Stack & Tools
            </h3>

            <TechTools />
          </div>

          <div className="text-center p-8 md:p-12 mb-6 border-t-2 border-b group-hover:border-b-primary bg-card">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Featured Projects
            </h2>
            <p className="max-w-2xl mx-auto">
              Showcasing innovation through AI, web development, and game design
            </p>
          </div>
        </div>

        <ProjectsShowcase />

        <div className="text-center mt-8">
          <Link
            href="/projects"
            className={buttonVariants({ variant: "outline", size: "lg" })}
          >
            View All Projects
          </Link>
        </div>
      </section>
    </>
  );
}
